import {
    Box, Container, Heading, HStack, Text, Badge, Spinner, Alert, AlertIcon,
    Button, Tabs, TabList, TabPanels, Tab, TabPanel, Input, Flex, IconButton,
    useBreakpointValue, Image, VStack, Divider, Wrap, WrapItem
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiRefreshCw, FiDownload, FiPackage, FiUser, FiClock, FiTruck, FiAlertTriangle, FiHash } from "react-icons/fi";
import { getProcessadorMonitoramentoDia, exportarProcessadorMonitoramento } from "../services/api";

const DATA_INICIO_COMPLETO = '2026-04-16';

const TIPOS_COMPLETOS = [
    { key: 'despachadas', label: 'Despachadas', colorScheme: 'green', icon: FiTruck },
    { key: 'problemas',   label: 'Falha de Conferência', colorScheme: 'red',   icon: FiAlertTriangle },
    { key: 'atrasadas',   label: 'Atrasadas',   colorScheme: 'orange',icon: FiClock },
    { key: 'devolucoes',  label: 'Devoluções',  colorScheme: 'blue',  icon: FiPackage },
];

const TIPOS_SOMENTE_DEVOLUCOES = [
    { key: 'devolucoes',  label: 'Devoluções',  colorScheme: 'blue',  icon: FiPackage },
];

const formatarDataHora = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch {
        return iso;
    }
};

const formatarDataBr = (dataIso) => {
    if (!dataIso) return '';
    const [y, m, d] = dataIso.split('-');
    return `${d}/${m}/${y}`;
};

const formatarMoeda = (v) => {
    if (v == null) return 'R$ 0,00';
    return 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const IMAGEM_PLACEHOLDER = "https://www.shutterstock.com/image-illustration/car-parts-auto-spare-shopping-600nw-2033173178.jpg";

export default function ProcessadorMonitoramentoDia() {
    const { data } = useParams();
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [erro, setErro] = useState(null);
    const [dadosPorTipo, setDadosPorTipo] = useState({});
    const [tabIndex, setTabIndex] = useState(0);
    const [busca, setBusca] = useState("");

    const TIPOS = data < DATA_INICIO_COMPLETO ? TIPOS_SOMENTE_DEVOLUCOES : TIPOS_COMPLETOS;
    const tipoAtual = TIPOS[tabIndex]?.key || 'devolucoes';

    const carregarTipo = useCallback(async (tipo, forcar = false) => {
        if (!forcar && dadosPorTipo[tipo]) return;
        setLoading(true);
        setErro(null);
        const res = await getProcessadorMonitoramentoDia(data, tipo);
        if (res?.error) {
            setErro(res.error);
        } else {
            setDadosPorTipo(prev => ({ ...prev, [tipo]: res }));
        }
        setLoading(false);
    }, [data, dadosPorTipo]);

    useEffect(() => {
        carregarTipo(tipoAtual);
    }, [tipoAtual]);

    const handleExportar = async () => {
        setExporting(true);
        setErro(null);
        const res = await exportarProcessadorMonitoramento(data, data);
        if (res?.error) {
            setErro('Erro ao exportar: ' + res.error);
        }
        setExporting(false);
    };

    const dadosDoTipoAtual = dadosPorTipo[tipoAtual]?.itens || [];
    const dadosFiltrados = useMemo(() => {
        if (!busca.trim()) return dadosDoTipoAtual;
        const termo = busca.toLowerCase();
        return dadosDoTipoAtual.filter(item => {
            const campos = [
                item.id, item.id_origem, item.cliente, item.origem,
                item.id_ml, item.id_order_ml
            ].filter(Boolean).map(c => String(c).toLowerCase());
            if (campos.some(c => c.includes(termo))) return true;
            if (Array.isArray(item.itens)) {
                return item.itens.some(it => {
                    const fs = [it.nome, it.sku, it.codigo_global, it.codigo_fornecedor, it.fabricante]
                        .filter(Boolean).map(x => String(x).toLowerCase());
                    return fs.some(f => f.includes(termo));
                });
            }
            return false;
        });
    }, [dadosDoTipoAtual, busca]);

    return (
        <Box minH="100vh" bg="#f0f0f0" py={{ base: 3, md: 6 }} px={{ base: 2, md: 4 }}>
            <Container maxW="container.xl" px={{ base: 0, md: 4 }}>
                <Flex
                    direction={{ base: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ base: 'stretch', md: 'center' }}
                    mb={4}
                    gap={2}
                >
                    <VStack spacing={2} align="stretch" w="100%" display={{ base: 'flex', md: 'none' }}>
                        <Button
                            leftIcon={<FiArrowLeft />}
                            size="sm"
                            variant="outline"
                            colorScheme="teal"
                            onClick={() => navigate('/processador/monitoramento')}
                            w="full"
                        >
                            Voltar ao Monitoramento
                        </Button>
                        <Heading size="sm" color="#01695E" textAlign="center">
                            Processamento — {formatarDataBr(data)}
                        </Heading>
                        <HStack spacing={2}>
                            <Button
                                leftIcon={<FiRefreshCw />}
                                size="sm"
                                colorScheme="teal"
                                variant="outline"
                                onClick={() => carregarTipo(tipoAtual, true)}
                                isLoading={loading}
                                flex={1}
                            >
                                Atualizar
                            </Button>
                            <Button
                                leftIcon={<FiDownload />}
                                size="sm"
                                colorScheme="orange"
                                bg="#F68633"
                                color="white"
                                _hover={{ bg: '#e67722' }}
                                onClick={handleExportar}
                                isLoading={exporting}
                                flex={1}
                            >
                                Exportar
                            </Button>
                        </HStack>
                    </VStack>
                    <HStack spacing={2} flex={1} minW={0} display={{ base: 'none', md: 'flex' }}>
                        <IconButton
                            icon={<FiArrowLeft />}
                            variant="ghost"
                            aria-label="Voltar"
                            size="md"
                            onClick={() => navigate('/processador/monitoramento')}
                        />
                        <Box minW={0}>
                            <Heading size="md" color="#01695E" noOfLines={1}>
                                Processamento — {formatarDataBr(data)}
                            </Heading>
                        </Box>
                    </HStack>
                    <HStack spacing={2} display={{ base: 'none', md: 'flex' }}>
                        <Button
                            leftIcon={<FiRefreshCw />}
                            size="sm"
                            colorScheme="teal"
                            variant="outline"
                            onClick={() => carregarTipo(tipoAtual, true)}
                            isLoading={loading}
                        >
                            Atualizar
                        </Button>
                        <Button
                            leftIcon={<FiDownload />}
                            size="sm"
                            colorScheme="orange"
                            bg="#F68633"
                            color="white"
                            _hover={{ bg: '#e67722' }}
                            onClick={handleExportar}
                            isLoading={exporting}
                        >
                            Exportar Excel
                        </Button>
                    </HStack>
                </Flex>

                {erro && (
                    <Alert status="error" borderRadius="md" mb={4} fontSize="sm">
                        <AlertIcon />
                        {erro}
                    </Alert>
                )}

                <Box bg="white" rounded="md" shadow="sm">
                    <Tabs
                        index={tabIndex}
                        onChange={(i) => { setTabIndex(i); setBusca(""); }}
                        colorScheme="teal"
                        variant="enclosed"
                        isFitted={isMobile}
                    >
                        <TabList overflowX="auto" flexWrap={{ base: 'nowrap', md: 'wrap' }}>
                            {TIPOS.map((t) => {
                                const total = dadosPorTipo[t.key]?.total ?? '—';
                                return (
                                    <Tab key={t.key} fontSize={{ base: 'xs', md: 'sm' }} px={{ base: 2, md: 4 }} whiteSpace="nowrap">
                                        <HStack spacing={1}>
                                            <Box as={t.icon} />
                                            <Text>{t.label}</Text>
                                            <Badge ml={1} colorScheme={t.colorScheme}>{total}</Badge>
                                        </HStack>
                                    </Tab>
                                );
                            })}
                        </TabList>
                        <TabPanels>
                            {TIPOS.map((t) => (
                                <TabPanel key={t.key} px={{ base: 2, md: 4 }} py={3}>
                                    <Box mb={3}>
                                        <Input
                                            size="sm"
                                            placeholder="Buscar por ID, cliente, produto, SKU..."
                                            value={busca}
                                            onChange={(e) => setBusca(e.target.value)}
                                        />
                                    </Box>

                                    {loading && !dadosPorTipo[t.key] && (
                                        <Flex justify="center" py={8}>
                                            <Spinner color="#F68633" size="lg" />
                                        </Flex>
                                    )}

                                    {!loading && t.key === 'devolucoes' && (
                                        <ListaDevolucoes lista={dadosFiltrados} />
                                    )}

                                    {!loading && t.key !== 'devolucoes' && (
                                        <ListaVendas tipo={t.key} lista={dadosFiltrados} />
                                    )}
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                </Box>
            </Container>
        </Box>
    );
}

function ListaVendas({ tipo, lista }) {
    if (!lista || lista.length === 0) {
        return (
            <Box textAlign="center" py={10} color="gray.500">
                <Box as={FiPackage} mx="auto" boxSize={8} mb={2} opacity={0.5} />
                <Text fontSize="sm">Nenhum registro encontrado.</Text>
            </Box>
        );
    }
    return (
        <VStack spacing={3} align="stretch">
            {lista.map((v) => (
                <CardVenda key={v.id} venda={v} tipo={tipo} />
            ))}
        </VStack>
    );
}

function CardVenda({ venda, tipo }) {
    const corOrigem = venda.origem?.includes('Mercado') ? 'yellow' : venda.origem === 'ECOMMERCE' ? 'purple' : 'teal';
    const statusCor = {
        cancelada: 'red', transito: 'blue', concluida: 'green', enviar: 'orange', confirmada: 'gray'
    }[venda.status] || 'gray';

    return (
        <Box borderWidth="1px" borderColor="gray.200" rounded="md" bg="white" overflow="hidden" shadow="sm" _hover={{ shadow: 'md', borderColor: 'teal.300' }}>
            <Box bg="gray.50" px={3} py={2} borderBottomWidth="1px" borderColor="gray.200">
                <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={2}>
                    <HStack spacing={2} flexWrap="wrap">
                        <Badge colorScheme="teal" fontSize="11px">
                            <HStack spacing={1}>
                                <Box as={FiHash} />
                                <Text>{venda.id_origem || venda.id}</Text>
                            </HStack>
                        </Badge>
                        <Badge colorScheme={corOrigem} fontSize="10px">{venda.origem}{venda.conta ? ` (${venda.conta})` : ''}</Badge>
                        <Badge colorScheme={statusCor} fontSize="10px" textTransform="uppercase">{venda.status}</Badge>
                    </HStack>
                    <HStack spacing={2} fontSize="11px" color="gray.700">
                        <Box as={FiUser} />
                        <Text fontWeight="medium" noOfLines={1} maxW={{ base: '180px', md: '260px' }}>{venda.cliente}</Text>
                    </HStack>
                </Flex>
            </Box>

            <Box px={3} py={2} borderBottomWidth={venda.itens?.length > 0 ? '1px' : '0'} borderColor="gray.100">
                <Wrap spacing={3} fontSize="11px" color="gray.700">
                    <WrapItem>
                        <HStack spacing={1}>
                            <Text fontWeight="bold">Prazo:</Text>
                            <Text>{formatarDataHora(venda.data_limite_envio)}</Text>
                        </HStack>
                    </WrapItem>
                    {tipo === 'despachadas' && (
                        <>
                            <WrapItem>
                                <HStack spacing={1}>
                                    <Text fontWeight="bold">Despachada em:</Text>
                                    <Text>{formatarDataHora(venda.despachada_em)}</Text>
                                </HStack>
                            </WrapItem>
                            <WrapItem>
                                <HStack spacing={1}>
                                    <Text fontWeight="bold">Operador:</Text>
                                    <Text>{venda.transito_by_nome || '—'}</Text>
                                </HStack>
                            </WrapItem>
                        </>
                    )}
                    {tipo === 'atrasadas' && (
                        <WrapItem>
                            <HStack spacing={1}>
                                <Text fontWeight="bold">Despachada em:</Text>
                                <Text color={venda.despachada_em ? 'orange.600' : 'red.600'} fontWeight="medium">
                                    {venda.despachada_em ? formatarDataHora(venda.despachada_em) : 'Não despachada'}
                                </Text>
                            </HStack>
                        </WrapItem>
                    )}
                    <WrapItem>
                        <HStack spacing={1}>
                            <Text fontWeight="bold">Venda:</Text>
                            <Text>{formatarDataHora(venda.data_venda)}</Text>
                        </HStack>
                    </WrapItem>
                </Wrap>

                {tipo === 'problemas' && Array.isArray(venda.marcacoes_problema) && venda.marcacoes_problema.length > 0 && (
                    <Box mt={2}>
                        <Text fontSize="11px" fontWeight="bold" color="red.700" mb={1}>Marcações do problema:</Text>
                        <Wrap spacing={1}>
                            {venda.marcacoes_problema.map((m) => (
                                <WrapItem key={m}>
                                    <Badge colorScheme="red" variant="solid" fontSize="10px" px={2} py={0.5}>{m}</Badge>
                                </WrapItem>
                            ))}
                        </Wrap>
                    </Box>
                )}
            </Box>

            {venda.itens && venda.itens.length > 0 && (
                <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                    {venda.itens.map((item, idx) => (
                        <ItemRow key={`${venda.id}-${item.produto_estoque_id}-${idx}`} item={item} />
                    ))}
                </VStack>
            )}
        </Box>
    );
}

function ItemRow({ item }) {
    const [imgErro, setImgErro] = useState(false);
    const src = (!imgErro && item.imagem_url) ? item.imagem_url : IMAGEM_PLACEHOLDER;

    return (
        <Flex px={3} py={2} gap={3} align="flex-start">
            <Image
                src={src}
                alt={item.nome}
                boxSize={{ base: '48px', md: '56px' }}
                objectFit="contain"
                bg="gray.50"
                rounded="sm"
                borderWidth="1px"
                borderColor="gray.100"
                flexShrink={0}
                onError={() => setImgErro(true)}
            />
            <Box flex={1} minW={0}>
                <Text fontSize={{ base: '12px', md: '13px' }} fontWeight="bold" color="gray.800" noOfLines={2}>
                    {item.nome}
                </Text>
                <Wrap spacing={2} mt={0.5} fontSize="10px" color="gray.600">
                    {item.sku && (
                        <WrapItem>
                            <Text><Text as="span" fontWeight="bold" color="gray.700">SKU:</Text> {item.sku}</Text>
                        </WrapItem>
                    )}
                    {item.codigo_global && (
                        <WrapItem>
                            <Text><Text as="span" fontWeight="bold" color="gray.700">Global:</Text> {item.codigo_global}</Text>
                        </WrapItem>
                    )}
                    {item.codigo_fornecedor && (
                        <WrapItem>
                            <Text><Text as="span" fontWeight="bold" color="gray.700">Forn:</Text> {item.codigo_fornecedor}</Text>
                        </WrapItem>
                    )}
                    {item.fabricante && (
                        <WrapItem>
                            <Text><Text as="span" fontWeight="bold" color="gray.700">Marca:</Text> {item.fabricante}</Text>
                        </WrapItem>
                    )}
                </Wrap>
            </Box>
            <VStack spacing={0.5} align="flex-end" flexShrink={0}>
                {item.quantidade > 1 && (
                    <Badge colorScheme="blue" fontSize="10px">x{item.quantidade}</Badge>
                )}
                <Badge colorScheme="teal" variant="outline" fontSize="9px">{item.fornecedor}</Badge>
                <Text fontSize="11px" color="gray.700" fontWeight="medium">
                    {formatarMoeda(item.valor_unitario)}
                </Text>
            </VStack>
        </Flex>
    );
}

function ListaDevolucoes({ lista }) {
    const navigate = useNavigate();
    if (!lista || lista.length === 0) {
        return (
            <Box textAlign="center" py={10} color="gray.500">
                <Box as={FiPackage} mx="auto" boxSize={8} mb={2} opacity={0.5} />
                <Text fontSize="sm">Nenhuma devolução finalizada neste dia.</Text>
            </Box>
        );
    }
    return (
        <VStack spacing={3} align="stretch">
            {lista.map((d) => (
                <Box key={d.id} borderWidth="1px" borderColor="gray.200" rounded="md" bg="white" p={3} shadow="sm" _hover={{ shadow: 'md', borderColor: 'blue.300' }}>
                    <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" gap={2}>
                        <Box flex={1} minW={0}>
                            <HStack spacing={2} flexWrap="wrap">
                                <Badge colorScheme="blue" fontSize="11px">#{d.id}</Badge>
                                <Badge colorScheme="gray" fontSize="10px">ML: {d.id_order_ml}</Badge>
                                <Badge colorScheme="purple" fontSize="10px">Conta {d.conta}</Badge>
                                {d.estoque && <Badge colorScheme="green" fontSize="10px">Estoque</Badge>}
                                {d.finalizada && !d.estoque && <Badge colorScheme="teal" fontSize="10px">Finalizada</Badge>}
                                {d.avaria && <Badge colorScheme="red" fontSize="10px">Avaria</Badge>}
                            </HStack>
                            <Wrap mt={2} spacing={3} fontSize="11px" color="gray.700">
                                <WrapItem>
                                    <Text><Text as="span" fontWeight="bold">Status:</Text> {d.status}</Text>
                                </WrapItem>
                                {d.type && (
                                    <WrapItem>
                                        <Text><Text as="span" fontWeight="bold">Tipo:</Text> {d.type}{d.subtype ? ` / ${d.subtype}` : ''}</Text>
                                    </WrapItem>
                                )}
                                <WrapItem>
                                    <Text><Text as="span" fontWeight="bold">Finalizada em:</Text> {formatarDataHora(d.received_at)}</Text>
                                </WrapItem>
                                {d.venda_relacionada && (
                                    <WrapItem>
                                        <Text><Text as="span" fontWeight="bold">Venda:</Text> {d.venda_relacionada.id_origem || d.venda_relacionada.id}</Text>
                                    </WrapItem>
                                )}
                            </Wrap>
                        </Box>
                        <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => navigate('/devolucoes')}
                            alignSelf={{ base: 'flex-end', md: 'center' }}
                        >
                            Ir para Devoluções
                        </Button>
                    </Flex>
                </Box>
            ))}
        </VStack>
    );
}
